import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, CancelButton } from 'molecules/Button';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import FormikSelect from 'molecules/FormikSelect';
import { useFormSubmitWithLoading } from 'utils/hooks/useFormSubmitWithLoading';
import RichTextEditor from 'react-rte';
import getAdminDashboard from 'contexts/AdminDashboardContext';
import Modal from 'molecules/ConfirmModalTemplate';
import Loader from 'atoms/Loader';
import { toast } from 'react-toastify';
import useAdminAppointment from 'contexts/AdminAppointmentContext';
import axiosService from 'utils/api/axios';

const ValidationSchema = yup.object().shape({
  soapNoteTemplateId: yup.object().required('required'),
  templateData: yup.string(),
});

export type FormProps = {
  onClose: () => void;
  open: boolean;
  appointmentDetails?: any;
};

const ChangeSoapNoteModal: FC<FormProps> = ({ onClose, open, appointmentDetails }) => {
  const {
    state: { soapNoteTemplatesList, soapNoteTemplatesLoading },
    actions: { getSoapNoteTemplates, updateSoapNoteInAppointmentDetails },
  } = getAdminDashboard();
  const {
    actions: { getAppointmentDetails },
  } = useAdminAppointment();
  const [templateValue, setTemplateValue] = useState<any>({
    soapNoteTemplateId: { id: '' },
  });
  useEffect(() => {
    getSoapNoteTemplates();
  }, [getSoapNoteTemplates]);

  useEffect(() => {
    if (soapNoteTemplatesList?.length > 0) {
      const mytempid = soapNoteTemplatesList?.find(
        (item: any) => item?.name === appointmentDetails?.soapNoteTemplateName,
      );
      setTemplateValue({
        soapNoteTemplateId: {
          ...mytempid,
          template: appointmentDetails?.soapNote
            ? appointmentDetails?.soapNote
            : appointmentDetails?.soapNoteTemplate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDetails?.soapNoteTemplateName, soapNoteTemplatesList]);
  // console.log('templateValue', templateValue);
  const handleSubmitTemplate = useCallback(
    async (values: any) => {
      if (!appointmentDetails?.id || !values?.soapNoteTemplateId?.id) return;
      const postData = {
        appointmentId: appointmentDetails?.id,
        soapnoteTemplateid: values.soapNoteTemplateId.id,
      };
      try {
        const res = await updateSoapNoteInAppointmentDetails(postData);
        if (!res) throw new Error('Failed to update SOAP template.');
        if (values.soapNoteTemplateId?.template) {
          await axiosService.put(`/appointment/update-SOAP-Note/${appointmentDetails?.id}`, {
            SoapNote: values.soapNoteTemplateId.template,
            SoapNoteImages: appointmentDetails?.soapNoteImages || [],
          });
        }
        await getAppointmentDetails(appointmentDetails?.id);
        toast.success('SOAP template updated.');
        onClose();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to update SOAP template.');
      }
    },
    [appointmentDetails?.id, appointmentDetails?.soapNoteImages, getAppointmentDetails, onClose, updateSoapNoteInAppointmentDetails],
  );
  const { onSubmitHandler, loading } = useFormSubmitWithLoading(handleSubmitTemplate);
  return (
    <Modal
      headerTextId="provider.soapnote"
      open={open}
      // @ts-ignore
      onClose={onClose}
    >
      {soapNoteTemplatesLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={templateValue}
          validationSchema={ValidationSchema}
          onSubmit={onSubmitHandler}
          enableReinitialize
        >
          {({ values }) => (
            <Form className="grid gap-3 sm:px-2 bg-white">
              {appointmentDetails?.appointmentStatus === 'SCHEDULED' && (
                <FormikSelect
                  label="admin.soap.note.templates.heading"
                  name="soapNoteTemplateId"
                  //@ts-ignore
                  maxMenuHeight={200}
                  options={soapNoteTemplatesList?.map((item: any) => {
                    return {
                      label: item?.name,
                      value: item,
                    };
                  })}
                />
              )}
              <div className="z-0">
                <RichTextEditor
                  editorClassName="h-80 z-0 overflow-y"
                  readOnly
                  value={RichTextEditor.createValueFromString(
                    values?.soapNoteTemplateId?.template,
                    'html',
                  )}
                />
              </div>
              {appointmentDetails?.appointmentStatus === 'SCHEDULED' && (
                <div className="flex sm:flex-row flex-col justify-center gap-3 sm:gap-6 mt-4">
                  <Button
                    type="submit"
                    loading={loading}
                    textid="confirm"
                    className="px-0 justify-center w-auto sm:px-16 sm:py-3"
                  />
                  <CancelButton
                    type="button"
                    textid="cancel"
                    onClick={onClose}
                    className="px-0 justify-center w-auto sm:px-16 sm:py-3"
                  ></CancelButton>
                </div>
              )}
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default ChangeSoapNoteModal;
